/**
 * Left-sidebar modules: paths match `App.js` routes; visibility follows `getAvailableRoles()` + user checks.
 * @typedef {{ id: string, label: string, to: string, icon: object, end?: boolean, activePathPrefix?: string, activePathPrefixes?: string[] }} SidebarNavItem
 */
import {
  FaHome,
  FaTachometerAlt,
  FaIndustry,
  FaShoppingCart,
  FaClipboardList,
  FaListAlt,
  FaFileInvoice,
  FaGavel,
  FaTruck,
  FaSearch,
  FaBuilding,
  FaPlusCircle,
  FaCheckCircle,
  FaTasks,
  FaComments,
  FaFileAlt,
  FaShip,
  FaClipboardCheck,
  FaStore,
  FaSitemap,
  FaTags,
  FaBoxOpen,
  FaProjectDiagram,
  FaAddressBook,
  FaBell,
  FaInbox,
  FaImages,
  FaGlobeAmericas,
  FaQuestionCircle,
  FaBalanceScale,
  FaTicketAlt,
  FaUserPlus,
} from "react-icons/fa";

/** Same seller detection as Header */
function userHasSellerCapability(user) {
  if (!user) return false;
  let userTypeId = user.user_type_id;
  if (userTypeId === null || userTypeId === undefined) userTypeId = "";
  else userTypeId = String(userTypeId).trim();
  const typeIds = userTypeId
    ? userTypeId.split(",").map((id) => String(id).trim()).filter((id) => id && id !== "null" && id !== "undefined")
    : [];
  const singleId = parseInt(userTypeId, 10);
  return (
    (!Number.isNaN(singleId) && userTypeId.indexOf(",") === -1 && (singleId === 2 || singleId === 3)) ||
    typeIds.some((id) => {
      const numId = parseInt(id, 10);
      return id === "2" || id === "3" || numId === 2 || numId === 3;
    })
  );
}

function userHasQualityInspectorCapability(user) {
  if (!user) return false;
  const userTypeId = user.user_type_id;
  if (userTypeId === null || userTypeId === undefined) return false;
  const typeId = parseInt(String(userTypeId).trim(), 10);
  return !Number.isNaN(typeId) && typeId === 4;
}

function userIsAccessSupportUser(user) {
  if (!user) return false;
  return (
    userIsSupportManager(user) ||
    userIsLogisticsManager(user) ||
    userIsLogisticsUser(user) ||
    (Number.isFinite(Number(user.access_role_id)) &&
      Number(user.access_role_id) > 0 &&
      (user.access_role_level === "support_user" ||
        user.access_role_code === "support" ||
        user.access_account_type === "support_user"))
  );
}

function userIsLogisticsManager(user) {
  return Boolean(
    user &&
      (user.access_role_level === "logistics_manager" ||
        user.access_role_code === "logistics-manager" ||
        user.access_account_type === "logistics_manager"),
  );
}

function userIsLogisticsUser(user) {
  return Boolean(
    user &&
      !userIsLogisticsManager(user) &&
      (user.access_role_level === "logistics_user" ||
        user.access_role_code === "logistics" ||
        user.access_account_type === "logistics_user"),
  );
}

function userIsSupportManager(user) {
  return Boolean(
    user &&
      (user.access_role_level === "support_manager" ||
        user.access_role_code === "support-manager" ||
        user.access_account_type === "support_manager"),
  );
}

/**
 * @typedef {{ id: string, heading: string, icon?: object, boldHeading?: boolean, items: SidebarNavItem[] }} SidebarCollapsibleGroup
 * @typedef {{ id: string, title: string|null, items?: SidebarNavItem[], topItems?: SidebarNavItem[], collapsibleGroups?: SidebarCollapsibleGroup[] }} SidebarSection
 */

/**
 * When `currentRole` is set (header role switcher), only that module's block is shown.
 * When null/undefined, every module the user is entitled to is shown.
 */
function shouldShowRoleSection(currentRole, sectionKey) {
  if (currentRole == null) return true;
  const id = currentRole.id;
  if (sectionKey === "buyer") return Number(id) === 1;
  if (sectionKey === "seller") return Number(id) === 2;
  if (sectionKey === "logistics") return id === "logistics";
  if (sectionKey === "inspection") {
    return id === "quality_inspector" || id === "quality_inspector_service";
  }
  return true;
}

/**
 * @typedef {{
 *   is_super_admin?: boolean,
 *   is_platform_admin?: boolean,
 *   platform_team_role?: string|null,
 *   is_company_admin?: boolean,
 *   has_p2p_org_membership?: boolean,
 *   can_approve?: boolean,
 * }} P2PNavContext
 */

/**
 * @param {{ roles: object[], user: object|null, currentRole?: object|null, p2pNav?: P2PNavContext|null }} params
 * @returns {SidebarSection[]}
 */
export function getSidebarSections({ roles, user, currentRole = null, p2pNav = null }) {
  const hasBuyer = roles.some((r) => r.id === 1);
  const hasSeller = roles.some((r) => r.id === 2);
  const hasLogistics = roles.some((r) => r.id === "logistics");
  const hasQiRole = roles.some((r) => r.id === "quality_inspector");
  const showSellerAuctions = userHasSellerCapability(user);
  const showInspection = userHasQualityInspectorCapability(user) || hasQiRole;
  const isAccessSupport = userIsAccessSupportUser(user);
  const isSupportManager = userIsSupportManager(user);
  const isLogisticsManager = userIsLogisticsManager(user);
  const isLogisticsUser = userIsLogisticsUser(user);

  const sections = [];

  const platformItems = [
    { id: "home", label: "Home", to: "/home", icon: FaHome, end: true },
    {
      id: "notifications",
      label: "Notifications",
      to: "/dashboard/notifications",
      icon: FaBell,
      activePathPrefix: "/dashboard/notifications",
    },
  ];

  if (isAccessSupport) {
    sections.push({
      id: "platform",
      title: null,
      items: [{ id: "home", label: "Home", to: "/home", icon: FaHome, end: true }],
    });
    if (isLogisticsManager) {
      sections.push({
        id: "logistics_team_management",
        title: null,
        collapsibleGroups: [
          {
            id: "logistics_team_management",
            heading: "Logistics Team",
            icon: FaSitemap,
            boldHeading: true,
            items: [
              {
                id: "logistics_manager_roles",
                label: "Logistics Roles",
                to: "/dashboard/logistics-manager/roles",
                icon: FaSitemap,
                activePathPrefix: "/dashboard/logistics-manager/roles",
              },
              {
                id: "logistics_manager_users",
                label: "Logistics Users",
                to: "/dashboard/logistics-manager/users",
                icon: FaAddressBook,
                activePathPrefix: "/dashboard/logistics-manager/users",
              },
            ],
          },
        ],
      });
      sections.push({
        id: "logistics_management",
        title: null,
        collapsibleGroups: [
          {
            id: "logistics_management",
            heading: "Logistics Management",
            icon: FaTruck,
            boldHeading: true,
            items: [
              {
                id: "logistics_manager_business_relocation_rfqs",
                label: "Business Relocation RFQs",
                to: "/dashboard/logistics-manager/business-relocation-rfqs",
                icon: FaClipboardList,
                activePathPrefix: "/dashboard/logistics-manager/business-relocation-rfqs",
              },
              {
                id: "logistics_manager_exhibition_logistics_rfqs",
                label: "Exhibition Logistics RFQs",
                to: "/dashboard/logistics-manager/exhibition-logistics-rfqs",
                icon: FaClipboardList,
                activePathPrefix: "/dashboard/logistics-manager/exhibition-logistics-rfqs",
              },
              {
                id: "logistics_manager_invite_providers",
                label: "Invite Logistics Providers",
                to: "/dashboard/logistics-manager/invite-providers",
                icon: FaUserPlus,
                activePathPrefix: "/dashboard/logistics-manager/invite-providers",
              },
            ],
          },
        ],
      });
    } else if (isLogisticsUser) {
      sections.push({
        id: "logistics_user_management",
        title: null,
        collapsibleGroups: [
          {
            id: "logistics_user_management",
            heading: "My Assigned RFQs",
            icon: FaTruck,
            boldHeading: true,
            items: [
              {
                id: "logistics_user_business_relocation_rfqs",
                label: "Business Relocation RFQs",
                to: "/dashboard/logistics-manager/business-relocation-rfqs",
                icon: FaClipboardList,
                activePathPrefix: "/dashboard/logistics-manager/business-relocation-rfqs",
              },
              {
                id: "logistics_user_exhibition_logistics_rfqs",
                label: "Exhibition Logistics RFQs",
                to: "/dashboard/logistics-manager/exhibition-logistics-rfqs",
                icon: FaClipboardList,
                activePathPrefix: "/dashboard/logistics-manager/exhibition-logistics-rfqs",
              },
            ],
          },
        ],
      });
    } else if (isSupportManager) {
      sections.push({
        id: "support_team_management",
        title: null,
        collapsibleGroups: [
          {
            id: "support_team_management",
            heading: "Support Team",
            icon: FaSitemap,
            boldHeading: true,
            items: [
              {
                id: "support_manager_roles",
                label: "Support Roles",
                to: "/dashboard/support-manager/roles",
                icon: FaSitemap,
                activePathPrefix: "/dashboard/support-manager/roles",
              },
              {
                id: "support_manager_users",
                label: "Support Users",
                to: "/dashboard/support-manager/users",
                icon: FaAddressBook,
                activePathPrefix: "/dashboard/support-manager/users",
              },
            ],
          },
        ],
      });
      sections.push({
        id: "dispute_management",
        title: null,
        collapsibleGroups: [
          {
            id: "dispute_management",
            heading: "Dispute Management",
            icon: FaBalanceScale,
            boldHeading: true,
            items: [
              {
                id: "support_manager_disputes",
                label: "All Disputes",
                to: "/dashboard/support-manager/disputes",
                icon: FaBalanceScale,
                end: true,
              },
              {
                id: "support_manager_my_disputes",
                label: "My Assigned Disputes",
                to: "/dashboard/support-manager/disputes?assignedToMe=1",
                icon: FaClipboardList,
              },
            ],
          },
          {
            id: "ticket_management",
            heading: "Ticket Management",
            icon: FaTicketAlt,
            boldHeading: true,
            items: [
              {
                id: "support_manager_tickets",
                label: "All Tickets",
                to: "/dashboard/support-manager/tickets",
                icon: FaTicketAlt,
                end: true,
              },
              {
                id: "support_manager_my_tickets",
                label: "My Assigned Tickets",
                to: "/dashboard/support-manager/tickets?assignedToMe=1",
                icon: FaClipboardList,
              },
            ],
          },
          {
            id: "feedback_management",
            heading: "Feedback Management",
            icon: FaComments,
            boldHeading: true,
            items: [
              {
                id: "support_manager_feedback",
                label: "All Feedback",
                to: "/dashboard/support-manager/feedback",
                icon: FaComments,
                end: true,
              },
            ],
          },
        ],
      });
    } else {
    sections.push({
      id: "dispute_management",
      title: null,
      collapsibleGroups: [
        {
          id: "dispute_management",
          heading: "Dispute Management",
          icon: FaBalanceScale,
          boldHeading: true,
          items: [
            {
              id: "my_assigned_disputes",
              label: "My Assigned Disputes",
              to: "/dashboard/support/disputes",
              icon: FaClipboardList,
              activePathPrefix: "/dashboard/support/disputes",
            },
          ],
        },
        {
          id: "ticket_management",
          heading: "Ticket Management",
          icon: FaTicketAlt,
          boldHeading: true,
          items: [
            {
              id: "my_assigned_tickets",
              label: "My Assigned Tickets",
              to: "/dashboard/support/tickets",
              icon: FaTicketAlt,
              activePathPrefix: "/dashboard/support/tickets",
            },
          ],
        },
        {
          id: "feedback_management",
          heading: "Feedback Management",
          icon: FaComments,
          boldHeading: true,
          items: [
            {
              id: "my_assigned_feedback",
              label: "My Assigned Feedback",
              to: "/dashboard/support/feedback",
              icon: FaComments,
              activePathPrefix: "/dashboard/support/feedback",
            },
          ],
        },
      ],
    });
    }
    sections.push({
      id: "platform_notifications",
      title: null,
      items: [
        {
          id: "notifications",
          label: "Notifications",
          to: "/dashboard/notifications",
          icon: FaBell,
          activePathPrefix: "/dashboard/notifications",
        },
      ],
    });
    return sections;
  }

  sections.push({
    id: "platform",
    title: null,
    items: platformItems,
  });

  if (hasBuyer && shouldShowRoleSection(currentRole, "buyer")) {
    sections.push({
      id: "buyer",
      title: "Buyer",
      topItems: [
        {
          id: "buyer_dashboard",
          label: "Buyer dashboard",
          to: "/dashboard/buyer",
          icon: FaTachometerAlt,
          end: true,
        },
      ],
      collapsibleGroups: [
        {
          id: "buyer_rfq",
          heading: "RFQ",
          icon: FaClipboardList,
          items: [
            {
              id: "buyer_cm",
              label: "Submit quote against manufacturer",
              to: "/dashboard/buyer/contract-manufacturers",
              icon: FaIndustry,
            },
            { id: "buyer_rfq", label: "Buyer RFQs", to: "/buyer-rfq", icon: FaShoppingCart },
            {
              id: "buyer_logistics_sent",
              label: "RFQs sent for Logistics",
              to: "/logistics-rfqs-sent",
              icon: FaTruck,
            },
          ],
        },
        {
          id: "buyer_quotations",
          heading: "Quotations",
          icon: FaFileAlt,
          items: [
            {
              id: "buyer_seller_quotations",
              label: "Seller quotations",
              to: "/seller-quotation-list",
              icon: FaFileAlt,
              activePathPrefixes: ["/seller-quotation-list", "/seller-quotation-accept"],
            },
            {
              id: "buyer_approve_qa_quotations",
              label: "Approve QA quotations",
              to: "/buyer/qa-quotations-list",
              icon: FaCheckCircle,
            },
          ],
        },
        {
          id: "buyer_clarifications",
          heading: "Clarifications",
          icon: FaQuestionCircle,
          items: [
            {
              id: "buyer_reply_seller_clarifications",
              label: "Reply to seller clarifications",
              to: "/buyer-clarification-list",
              icon: FaComments,
            },
          ],
        },
        {
          id: "buyer_invoice",
          heading: "Invoice",
          icon: FaFileInvoice,
          items: [
            {
              id: "buyer_approve_invoices_seller",
              label: "Approve invoices from seller",
              to: "/approve-invoice-list",
              icon: FaFileInvoice,
            },
          ],
        },
        {
          id: "buyer_eway_bill",
          heading: "E-way bill",
          icon: FaTruck,
          items: [
            {
              id: "buyer_eway_track",
              label: "Track e-way bills",
              to: "/eway-bill-tracking",
              icon: FaClipboardCheck,
            },
          ],
        },
        {
          id: "buyer_shipping_manifest",
          heading: "Shipping manifest",
          icon: FaClipboardList,
          items: [
            {
              id: "buyer_accept_shipping_manifest",
              label: "Accept shipping manifest",
              to: "/shipping-manifest-list",
              icon: FaShip,
            },
          ],
        },
        {
          id: "buyer_grn",
          heading: "GRN",
          icon: FaBoxOpen,
          items: [
            {
              id: "buyer_generate_grn",
              label: "Generate GRN",
              to: "/grn-manifest-list",
              icon: FaPlusCircle,
              activePathPrefixes: ["/grn-manifest-list", "/buyer-goods-receipt-generate"],
            },
          ],
        },
        {
          id: "buyer_auction",
          heading: "Auction",
          icon: FaGavel,
          items: [
            {
              id: "buyer_bid_auctions",
              label: "Bid on auctions",
              to: "/auction",
              icon: FaGavel,
              end: true,
            },
            {
              id: "buyer_my_bids",
              label: "My bids",
              to: "/auction/my/bids",
              icon: FaStore,
              activePathPrefixes: ["/auction/my/bids"],
            },
          ],
        },
        {
          id: "buyer_infrastructure_leasing",
          heading: "Infrastructure leasing",
          icon: FaBuilding,
          items: [
            {
              id: "buyer_infra_my_requirements",
              label: "My requirements",
              to: "/infrastructure-leasing/my-requirements",
              icon: FaFileAlt,
              activePathPrefixes: ["/infrastructure-leasing/my-requirements"],
            },
            {
              id: "buyer_infra_my_enquiries",
              label: "My enquiry list",
              to: "/infrastructure-leasing/my-enquiries",
              icon: FaClipboardList,
              activePathPrefixes: ["/infrastructure-leasing/my-enquiries"],
            },
          ],
        },
        {
          id: "buyer_orders",
          heading: "Orders",
          icon: FaShoppingCart,
          items: [
            {
              id: "buyer_po",
              label: "Create PO",
              to: "/purchase-order-list",
              icon: FaClipboardList,
            },
          ],
        },
      ],
    });
  }

  if (hasSeller && shouldShowRoleSection(currentRole, "seller")) {
    const topItems = [
      {
        id: "seller_dashboard",
        label: "Seller dashboard",
        to: "/dashboard/seller",
        icon: FaTachometerAlt,
        end: true,
      },
    ];
    sections.push({
      id: "seller",
      title: "Seller",
      topItems,
      collapsibleGroups: [
        {
          id: "seller_rfq",
          heading: "RFQ",
          items: [
            {
              id: "seller_cm_rfqs",
              label: "Contract manufacturing RFQ",
              to: "/seller/cm-rfqs",
              icon: FaIndustry,
            },
            {
              id: "seller_buyer_rfq_list",
              label: "Buyer RFQ list",
              to: "/seller-rfq-list",
              icon: FaListAlt,
            },
            {
              id: "seller_logistics_rfqs_sent",
              label: "RFQ sent for logistics",
              to: "/logistics-rfqs-sent",
              icon: FaTruck,
            },
          ],
        },
        {
          id: "seller_invoice",
          heading: "Invoice",
          items: [
            { id: "seller_invoice_list", label: "Invoice list", to: "/invoice-list", icon: FaFileInvoice },
            {
              id: "seller_generate_invoice",
              label: "Generate invoice",
              to: "/generate-invoice",
              icon: FaPlusCircle,
            },
          ],
        },
        ...(showSellerAuctions
          ? [
              {
                id: "seller_auction",
                heading: "Auction",
                items: [
                  {
                    id: "seller_auction_create",
                    label: "Create Auction",
                    to: "/auction/create",
                    icon: FaGavel,
                    end: true,
                  },
                  {
                    id: "seller_auction_my",
                    label: "My Auctions",
                    to: "/auction/my/auctions",
                    icon: FaStore,
                    activePathPrefixes: ["/auction/my/auctions"],
                  },
                ],
              },
            ]
          : []),
        {
          id: "seller_infrastructure_leasing",
          heading: "Infrastructure leasing",
          items: [
            {
              id: "seller_infra_my_properties_assets",
              label: "My properties / assets",
              to: "/infrastructure-leasing/my-properties",
              icon: FaBuilding,
              activePathPrefixes: ["/infrastructure-leasing/my-properties"],
            },
            {
              id: "seller_infra_inventory_operations",
              label: "Inventory operations",
              to: "/infrastructure-leasing/my-properties/inventory-overview",
              icon: FaClipboardList,
              activePathPrefixes: ["/infrastructure-leasing/my-properties/inventory-overview"],
            },
            {
              id: "seller_infra_create_listing",
              label: "Create new listing",
              to: "/infrastructure-leasing/list-asset",
              icon: FaPlusCircle,
              activePathPrefixes: ["/infrastructure-leasing/list-asset"],
            },
          ],
        },
        {
          id: "seller_orders",
          heading: "Orders",
          items: [
            {
              id: "seller_purchase_orders",
              label: "Purchase orders",
              to: "/seller-purchase-order-list",
              icon: FaClipboardList,
              activePathPrefixes: ["/seller-purchase-order-list", "/purchase-order-accept"],
            },
          ],
        },
        {
          id: "seller_clarifications",
          heading: "Clarifications",
          items: [
            {
              id: "seller_clarification_list",
              label: "Clarifications",
              to: "/clarification-list",
              icon: FaComments,
            },
          ],
        },
        {
          id: "seller_eway",
          heading: "E-way bill",
          items: [
            {
              id: "seller_eway_list",
              label: "Generate e-way bills",
              to: "/eway-generation-list",
              icon: FaFileAlt,
              activePathPrefixes: ["/eway-generation-list", "/eway-bill-generate"],
            },
          ],
        },
        {
          id: "seller_shipping_manifest",
          heading: "Shipping manifest",
          items: [
            {
              id: "seller_shipping_manifest_list",
              label: "Generate shipping manifests",
              to: "/shipping-manifest-generation-list",
              icon: FaShip,
              activePathPrefixes: ["/shipping-manifest-generation-list", "/shipping-manifest-create"],
            },
          ],
        },
        {
          id: "seller_grn",
          heading: "GRN",
          items: [
            {
              id: "seller_approve_grn",
              label: "Approve goods receipt",
              to: "/approve-goods-receipt-list",
              icon: FaClipboardCheck,
            },
          ],
        },
      ],
    });
  }

  if (hasLogistics && shouldShowRoleSection(currentRole, "logistics")) {
    sections.push({
      id: "logistics",
      title: "Logistics",
      topItems: [
        {
          id: "logistics_dashboard",
          label: "Logistics Provider Dashboard",
          to: "/dashboard/logistics",
          icon: FaTachometerAlt,
          end: true,
        },
      ],
      collapsibleGroups: [
        {
          id: "logistics_operations",
          heading: "Logistics Operations",
          items: [
            {
              id: "logistics_rfqs_inbox",
              label: "RFQs received",
              to: "/dashboard/logistics/rfqs",
              icon: FaInbox,
            },
          ],
        },
      ],
    });
  }

  if (showInspection && shouldShowRoleSection(currentRole, "inspection")) {
    sections.push({
      id: "inspection",
      title: "Inspection",
      topItems: [
        {
          id: "qi_dashboard",
          label: "Quality Inspector Dashboard",
          to: "/auction/qa/dashboard",
          icon: FaTachometerAlt,
          end: true,
        },
      ],
      collapsibleGroups: [
        {
          id: "qi_requests",
          heading: "Requests",
          items: [
            {
              id: "qi_open",
              label: "Open requests",
              to: "/auction/qa/dashboard/requests",
              icon: FaClipboardList,
            },
            {
              id: "qi_inspections",
              label: "Inspections",
              to: "/auction/qa/dashboard/inspections",
              icon: FaTasks,
            },
            {
              id: "qi_completed",
              label: "Completed",
              to: "/auction/qa/dashboard/completed",
              icon: FaCheckCircle,
            },
          ],
        },
      ],
    });
  }

  const p2p = p2pNav || {};
  const isSuperAdmin = !!p2p.is_super_admin;
  const isPlatformAdmin = !!p2p.is_platform_admin;
  const teamRole = p2p.platform_team_role || null;
  const isCompanyAdmin = !!p2p.is_company_admin;
  const hasOrgMembership = !!p2p.has_p2p_org_membership;
  const canApprove = !!p2p.can_approve;
  const canAccessCommandCentre = !!p2p.can_access_command_centre;
  const canSubmitServiceRequests = !!p2p.can_submit_service_requests;
  const canAccessPreapprovedVendors = !!p2p.can_access_preapproved_vendors;
  const canAccessSalesAuctionControls = !!p2p.can_access_sales_auction_controls;
  const isPlatformTeamConsoleAdmin = !!p2p.is_platform_team_console_admin;
  const isAccountManagerOnly = !!p2p.is_account_manager_only;
  const isPlatformTeam =
    teamRole === "head" || teamRole === "manager" || teamRole === "account_manager";

  const p2pItems = [];

  if (isSuperAdmin) {
    p2pItems.push(
      {
        id: "p2p_platform_admin_management",
        label: "P2P Platform Admin Management",
        to: "/dashboard/admin/p2p-platform-admin-management",
        icon: FaSitemap,
        end: true,
      },
    );
  }

  if (isSuperAdmin || isPlatformAdmin) {
    p2pItems.push({
      id: "p2p_access_diagnostics",
      label: "P2P access diagnostics (UAT)",
      to: "/dashboard/admin/p2p-diagnostics",
      icon: FaFileAlt,
      end: true,
    });
  }

  if (isSuperAdmin || isCompanyAdmin) {
    p2pItems.push(
      {
        id: "p2p_organization_setup",
        label: "Company Control Setup",
        to: "/dashboard/admin/p2p-organization-setup",
        icon: FaSitemap,
        end: true,
      },
      {
        id: "p2p_approval_setup",
        label: "Approval Matrix",
        to: "/dashboard/admin/p2p-approval-setup",
        icon: FaTasks,
        end: true,
      },
    );
  }

  if (canAccessSalesAuctionControls) {
    p2pItems.push({
      id: "p2p_sales_auction_controls",
      label: "Sales & Auction Controls",
      to: "/dashboard/admin/p2p-approval-setup?domain=sales",
      icon: FaGavel,
      end: true,
    });
  }

  if (canApprove || isSuperAdmin || isCompanyAdmin) {
    p2pItems.push({
      id: "p2p_my_approvals",
      label: "My approvals",
      to: "/dashboard/p2p/my-approvals",
      icon: FaCheckCircle,
      end: true,
    });
  }

  if (canAccessCommandCentre || isSuperAdmin) {
    p2pItems.push({
      id: "p2p_command_centre",
      label: "Procurement Command Centre",
      to: "/dashboard/p2p/command-centre",
      icon: FaProjectDiagram,
      activePathPrefix: "/dashboard/p2p/transactions",
    });
  }

  if (canAccessPreapprovedVendors || isSuperAdmin) {
    p2pItems.push({
      id: "p2p_preapproved_vendors",
      label: "Pre-approved vendors",
      to: "/dashboard/p2p/preapproved-vendors",
      icon: FaAddressBook,
      end: true,
    });
  }

  if (canSubmitServiceRequests || isSuperAdmin) {
    p2pItems.push({
      id: "p2p_services_catalogue",
      label: "Procurement-as-a-Service",
      to: "/dashboard/p2p/services",
      icon: FaStore,
      end: true,
    });
  }

  if (isSuperAdmin || isPlatformAdmin) {
    p2pItems.push({
      id: "p2p_platform_services_admin",
      label: "Platform services (P2P Platform Admin)",
      to: "/dashboard/admin/p2p-platform-services",
      icon: FaSitemap,
      end: true,
    });
  }

  if (isSuperAdmin || isPlatformAdmin || isPlatformTeamConsoleAdmin) {
    p2pItems.push({
      id: "p2p_service_console",
      label: "P2P Platform Admin Console",
      to: "/dashboard/p2p/service-console",
      icon: FaTasks,
      end: true,
    });
  } else if (isAccountManagerOnly) {
    p2pItems.push({
      id: "p2p_service_console",
      label: "My assigned service requests",
      to: "/dashboard/p2p/service-console",
      icon: FaTasks,
      end: true,
    });
  }

  if (isSuperAdmin || isPlatformAdmin || isCompanyAdmin || isPlatformTeam) {
    p2pItems.push({
      id: "p2p_insights",
      label: "P2P Insights",
      to: "/dashboard/p2p/insights",
      icon: FaFileAlt,
      end: true,
    });
  }

  if (p2pItems.length > 0) {
    sections.push({
      id: "p2p",
      title: null,
      collapsibleGroups: [
        {
          id: "p2p",
          heading: "Procure-to-Pay",
          items: p2pItems,
        },
      ],
    });
  }

  sections.push({
    id: "support",
    title: null,
    collapsibleGroups: [
      {
        id: "support",
        heading: "SUPPORT",
        items: [
          {
            id: "my_disputes",
            label: "My Disputes",
            to: "/dashboard/help-center/my-disputes",
            icon: FaBalanceScale,
            activePathPrefix: "/dashboard/help-center/my-disputes",
          },
          {
            id: "my_tickets",
            label: "My Tickets",
            to: "/dashboard/help-center/my-tickets",
            icon: FaClipboardList,
            activePathPrefix: "/dashboard/help-center/my-tickets",
          },
          {
            id: "my_feedback",
            label: "My Feedback",
            to: "/dashboard/help-center/my-feedback",
            icon: FaComments,
            activePathPrefix: "/dashboard/help-center/my-feedback",
          },
        ],
      },
    ],
  });

  sections.push({
    id: "marketplace",
    title: "Marketplace",
    collapsibleGroups: [
      {
        id: "marketplace_auctions",
        heading: "Auctions",
        items: [{ id: "auctions_browse", label: "Auction listings", to: "/auction", icon: FaGavel, end: true }],
      },
      {
        id: "marketplace_sourcing",
        heading: "Sourcing",
        items: [
          {
            id: "contract_mfg_browse",
            label: "Contract manufacturers",
            to: "/contract-manufacturers/contract",
            icon: FaBuilding,
            activePathPrefix: "/contract-manufacturers",
          },
          {
            id: "logistics_browse",
            label: "Logistics providers",
            to: "/logistics-providers",
            icon: FaTruck,
            activePathPrefix: "/logistics-providers",
          },
          {
            id: "infra_leasing",
            label: "Infrastructure leasing",
            to: "/infrastructure-leasing/listings",
            icon: FaSearch,
            activePathPrefix: "/infrastructure-leasing",
          },
        ],
      },
    ],
  });

  return sections;
}

/** True if any link in the section matches the current path. */
export function sidebarSectionHasActiveChild(section, pathname) {
  const items = [
    ...(section.items || []),
    ...(section.topItems || []),
    ...(section.collapsibleGroups || []).flatMap((g) => g.items || []),
  ];
  return items.some((item) => getSidebarIsActive(pathname, item));
}

/** Orange section header when the module is active or selected in the role switcher. */
export function sidebarSectionIsHighlighted(section, currentRole, pathname) {
  if (!section.title) return false;
  if (sidebarSectionHasActiveChild(section, pathname)) return true;
  if (currentRole == null) return false;
  const id = currentRole.id;
  if (section.id === "buyer" && Number(id) === 1) return true;
  if (section.id === "seller" && Number(id) === 2) return true;
  if (section.id === "logistics" && id === "logistics") return true;
  if (
    section.id === "inspection" &&
    (id === "quality_inspector" || id === "quality_inspector_service")
  ) {
    return true;
  }
  return false;
}

/**
 * Active state for sidebar links (works with NavLink `isActive`).
 */
export function getSidebarIsActive(pathname, item, search = "") {
  const { to, end, activePathPrefix, activePathPrefixes } = item;
  const currentSearch = String(search || "").startsWith("?")
    ? String(search).slice(1)
    : String(search || "");
  const currentParams = new URLSearchParams(currentSearch);

  if (to && String(to).includes("?")) {
    const [pathOnly, query = ""] = String(to).split("?");
    const expected = new URLSearchParams(query);
    const pathMatches =
      pathname === pathOnly || pathname === `${pathOnly}/`;
    if (!pathMatches) return false;
    for (const [key, value] of expected.entries()) {
      if (currentParams.get(key) !== value) return false;
    }
    return true;
  }

  if (activePathPrefixes && activePathPrefixes.length) {
    return activePathPrefixes.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`)
    );
  }
  if (activePathPrefix) {
    const pathMatches =
      pathname === activePathPrefix ||
      pathname.startsWith(`${activePathPrefix}/`);
    if (!pathMatches) return false;
    // Prefer exact non-query list links over query-scoped siblings.
    if (end && currentParams.toString()) return false;
    return true;
  }
  if (!to) return false;
  if (end) {
    return (
      (pathname === to || pathname === `${to}/`) &&
      !currentParams.toString()
    );
  }
  if (pathname === to) return true;
  return pathname.startsWith(`${to}/`);
}

/** True if any link in the group matches the current path (for expand + highlight). */
export function sidebarGroupHasActiveChild(pathname, items, search = "") {
  if (!items || !items.length) return false;
  return items.some((item) => getSidebarIsActive(pathname, item, search));
}
